import React, { forwardRef, useCallback } from 'react'
import styled, { css, CSSObject } from 'styled-components'
import { colorSystem, fontSystem } from '@/styles/theme'
import { HStack } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'

export interface TextAreaProps {
  value?: string
  disabled?: boolean
  emptyCheck?: boolean
  autoHeight?: boolean
  placeholder?: string
  rows?: number
  cols?: number
  maxLength?: number
  minHeight?: number
  resize?: 'horizontal' | 'vertical' | 'both' | 'none'
  style?: CSSObject
  className?: string
  onChange?: (v: string) => void
  onBlur?: () => void
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ value, placeholder, resize, rows, cols, maxLength, disabled, emptyCheck, minHeight, autoHeight, onBlur, onChange, style, className }, ref) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!onChange) return
        // emptyCheck=true 시 빈값 체크
        if (emptyCheck && e.currentTarget.value.length && e.currentTarget.value.trim() === '') return
        onChange(e.currentTarget.value || '')
      },
      [onChange, emptyCheck],
    )

    return (
      <TextAreaWrap>
        <StyledTextArea
          className={className}
          ref={ref}
          value={value}
          placeholder={placeholder}
          $autoHeight={autoHeight}
          $resize={resize}
          disabled={disabled}
          rows={rows}
          cols={cols}
          maxLength={maxLength}
          $minHeight={minHeight || 140}
          style={style}
          onChange={handleChange}
          onBlur={onBlur}
        />

        {maxLength && !disabled && (
          <TextAreaLength>
            <Text kind="Body_14_regular" color={colorSystem.text.common.tertiary}>
              {value?.length}/{maxLength}
            </Text>
          </TextAreaLength>
        )}
      </TextAreaWrap>
    )
  },
)

const TextAreaWrap = styled.div`
  position: relative;
`

const StyledTextArea = styled.textarea<{ $minHeight: number; $resize: TextAreaProps['resize']; $autoHeight?: boolean }>`
  padding: 8px;
  outline: none;
  width: 100%;
  border-radius: 4px;
  border: 1px solid ${colorSystem.border.element};
  background-color: ${colorSystem.bg.input.enabled};
  box-sizing: border-box;
  resize: ${({ $resize }) => $resize || 'none'};
  color: ${colorSystem.text.common.primary};
  ${fontSystem.Body_14_regular};

  ${({ $autoHeight, $minHeight }) =>
    $autoHeight
      ? css`
          height: auto;
        `
      : css`
          min-height: ${$minHeight}px;
        `}

  &:disabled {
    background-color: ${colorSystem.bg.input.disabled};
  }

  &:hover,
  &:focus,
  &:active {
    border-color: ${colorSystem.border.element_active};
  }

  &::placeholder {
    color: ${colorSystem.text.common.tertiary};
  }
`

const TextAreaLength = styled(HStack)`
  position: absolute;
  right: 16px;
  bottom: 16px;
`

export default TextArea
